// PositionSizeSection.jsx
// Handles position sizing for both spot and futures bots.
// Spot:   fixed base-asset amount  (e.g. 0.01 BTC)
// Futures: % of available margin   (e.g. 10% margin)
//          OR fixed USDT notional  (e.g. 500 USDT)
//
// Props:
//   newBot          — full bot state object
//   setNewBot       — (patch) => void  (merged patch, not full replace)
//   marketType      — 'spot' | 'futures'
//   leverage        — number (passed separately for the effective-exposure calc)
//   quoteAsset      — 'USDT' | 'BTC' | etc.  (from selected symbol)
//   baseAsset       — 'BTC' | 'ETH' | etc.

import React, { useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment,
  Slider, Chip, Tooltip, Switch,
} from '@mui/material';
import { Percent, AttachMoney, Warning } from '@mui/icons-material';

/* ═══════════════════════════════════════════════════════════════════════════════
   THEME — identical to StrategyBuilder / FuturesComponents
   ═══════════════════════════════════════════════════════════════════════════════ */

const C = {
  bg0: '#070a0f', bg1: '#070a0f', bg2: '#0f1218', bg3: '#0f1218', bg4: '#0b0e15',
  b0: 'rgba(255,255,255,0.06)', b1: 'rgba(255,255,255,0.06)', b2: 'rgba(255,255,255,0.1)',
  t1: '#f2f6ff', t2: '#e8edf7', t3: '#9aa6bd', t4: '#6f7c96',
  purple: '#4d8fc7', green: '#46b88a', red: '#d97b72',
  blue: '#4d8fc7', yellow: '#d9a441', orange: '#d9a441',
  longDim: 'rgba(70,184,138,0.08)', longBdr: 'rgba(70,184,138,0.25)',
  shortDim: 'rgba(217,123,114,0.08)', shortBdr: 'rgba(217,123,114,0.25)',
  futuresWarn: 'rgba(217,164,65,0.08)', futuresWarnBdr: 'rgba(217,164,65,0.25)',
  mono: '"SF Mono", Monaco, Consolas, monospace',
};

const panelSx = {
  bgcolor: C.bg3, border: '1px solid', borderColor: C.b0, borderRadius: 2, overflow: 'hidden',
};

const headerBarSx = {
  display: 'flex', alignItems: 'center',
  px: 2, py: 1, bgcolor: C.bg1,
  borderBottom: '1px solid', borderColor: C.b0, minHeight: 40,
};

const microLabel = {
  fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  lineHeight: 1, mb: '5px', display: 'block',
};

const fieldSx = {
  bgcolor: C.bg1, borderRadius: 1.5,
  '& fieldset': { borderColor: C.b0, borderRadius: 1.5 },
  '&:hover fieldset': { borderColor: `${C.b2} !important` },
  '&.Mui-focused fieldset': { borderColor: `${C.b2} !important`, borderWidth: '1px !important' },
};

/* ═══════════════════════════════════════════════════════════════════════════════
   SIZING MODES
   ═══════════════════════════════════════════════════════════════════════════════ */

const FUTURES_SIZE_MODES = [
  {
    value: 'margin_pct',
    label: '% of Margin',
    icon: Percent,
    desc: 'Risk a percentage of your available margin per trade. Scales automatically with account size.',
    color: C.green,
    dimBg: C.longDim,
    bdr: C.longBdr,
  },
  {
    value: 'fixed_notional',
    label: 'Fixed Notional',
    icon: AttachMoney,
    desc: 'Open a fixed USDT-equivalent position size regardless of account balance.',
    color: C.blue,
    dimBg: 'rgba(77,143,199,0.08)',
    bdr: 'rgba(77,143,199,0.25)',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════════
   MARGIN PCT PRESETS
   ═══════════════════════════════════════════════════════════════════════════════ */

const MARGIN_PCT_PRESETS = [1, 2, 5, 10, 15, 20, 25, 50];

/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function riskLabel(pct) {
  if (pct <= 2)  return { label: 'CONSERVATIVE', color: C.green };
  if (pct <= 5)  return { label: 'MODERATE',     color: C.green };
  if (pct <= 10) return { label: 'AGGRESSIVE',   color: C.yellow };
  if (pct <= 20) return { label: 'HIGH RISK',    color: C.orange };
  return               { label: 'EXTREME RISK',  color: C.red };
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SPOT SIZE PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function SpotSizePanel({ newBot, patch, baseAsset, quoteAsset }) {
  const asset = baseAsset || 'base';
  const quote = quoteAsset || 'USDT';
  const mode = newBot.sizeMode || 'base'; // 'base' | 'quote'
  const activeAsset = mode === 'base' ? asset : quote;

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Currency toggle */}
      <Box>
        <Typography sx={microLabel}>ORDER SIZE IN</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {[
            { value: 'base',  label: asset.toUpperCase(),  desc: `Amount of ${asset.toUpperCase()} per signal` },
            { value: 'quote', label: quote.toUpperCase(), desc: `Spend fixed ${quote.toUpperCase()} per signal` },
          ].map(opt => {
            const active = mode === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => patch({ sizeMode: opt.value, tradeAmount: '' })}
                sx={{
                  px: 1.5, py: 1.25,
                  border: '1px solid',
                  borderColor: active ? C.longBdr : C.b0,
                  borderRadius: 1.5, cursor: 'pointer',
                  bgcolor: active ? C.longDim : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: C.longBdr, bgcolor: C.longDim },
                }}
              >
                <Typography sx={{
                  fontFamily: C.mono, fontSize: 12, fontWeight: 700,
                  color: active ? C.green : C.t3,
                }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ fontFamily: C.mono, fontSize: 9.5, color: C.t4, lineHeight: 1.4 }}>
                  {opt.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Trade Amount */}
      <Box>
        <Typography sx={microLabel}>TRADE AMOUNT</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextField
            size="small" type="number"
            value={newBot.tradeAmount ?? ''}
            onChange={e => patch({ tradeAmount: e.target.value })}
            placeholder="0.00"
            inputProps={{
              step: mode === 'base' ? 0.001 : 1, min: 0,
              style: { fontFamily: C.mono, fontSize: 14, fontWeight: 600, padding: '7px 12px' },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.t4 }}>
                    {activeAsset.toUpperCase()}
                  </Typography>
                </InputAdornment>
              ),
              sx: { ...fieldSx, height: 38 },
            }}
            sx={{ width: 180 }}
          />

          {newBot.tradeAmount && (
            <Box sx={{
              px: 1.5, py: 1, bgcolor: C.bg1, border: '1px solid', borderColor: C.b0, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>≈</Typography>
              <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3 }}>
                {newBot.tradeAmount} {activeAsset.toUpperCase()} per signal
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Max trades in flight */}
      

      {/* Allow pyramiding */}
    

    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   FUTURES SIZE PANEL
   ═══════════════════════════════════════════════════════════════════════════════ */

function FuturesSizePanel({ newBot, patch, leverage, quoteAsset }) {
  const mode = newBot.futureSizeMode || 'margin_pct';
  const quote = quoteAsset || 'USDT';
  const lev   = Number(leverage) || 1;

  // Effective exposure = notional size × leverage
  const marginPct    = Number(newBot.marginPct) || 0;
  const fixedNotional = Number(newBot.fixedNotional) || 0;
  const risk = riskLabel(marginPct);

  const effectiveExposureHint = mode === 'margin_pct'
    ? `At ${lev}× leverage, ${marginPct}% margin controls ${(marginPct * lev).toFixed(0)}% of margin as notional`
    : `At ${lev}× leverage, ${fixedNotional} ${quote} controls ${(fixedNotional * lev).toLocaleString()} ${quote} notional`;

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Mode selector */}
      <Box>
        <Typography sx={microLabel}>SIZING METHOD</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {FUTURES_SIZE_MODES.map(m => {
            const Icon = m.icon;
            const active = mode === m.value;
            return (
              <Box
                key={m.value}
                onClick={() => patch({ futureSizeMode: m.value })}
                sx={{
                  px: 1.5, py: 1.25,
                  border: '1px solid',
                  borderColor: active ? m.bdr : C.b0,
                  borderRadius: 1.5, cursor: 'pointer',
                  bgcolor: active ? m.dimBg : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: m.bdr, bgcolor: m.dimBg },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <Icon sx={{ fontSize: 13, color: active ? m.color : C.t4 }} />
                  <Typography sx={{
                    fontFamily: C.mono, fontSize: 11, fontWeight: 700,
                    color: active ? m.color : C.t3, letterSpacing: '0.02em',
                  }}>
                    {m.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: C.mono, fontSize: 9.5, color: C.t4, lineHeight: 1.4 }}>
                  {m.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── % of Margin mode ── */}
      {mode === 'margin_pct' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={microLabel}>MARGIN PER TRADE</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                px: 1, py: '2px',
                bgcolor: `${risk.color}15`, border: '1px solid', borderColor: `${risk.color}40`,
                borderRadius: 0.75,
              }}>
                <Typography sx={{ fontFamily: C.mono, fontSize: 9, fontWeight: 700, color: risk.color, letterSpacing: '0.06em' }}>
                  {risk.label}
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: C.mono, fontSize: 18, fontWeight: 700, color: risk.color, lineHeight: 1, minWidth: 44, textAlign: 'right' }}>
                {marginPct}%
              </Typography>
            </Box>
          </Box>

          <Slider
            value={marginPct}
            onChange={(_, v) => patch({ marginPct: v })}
            min={0.5} max={100} step={0.5}
            sx={{
              color: risk.color, mb: 1,
              '& .MuiSlider-thumb': { width: 14, height: 14, borderRadius: 1.5, boxShadow: 'none', transition: 'color 0.2s' },
              '& .MuiSlider-track': { height: 3, borderRadius: 0 },
              '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1 },
            }}
          />

          {/* Preset chips */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
            {MARGIN_PCT_PRESETS.map(p => (
              <Box
                key={p}
                onClick={() => patch({ marginPct: p })}
                sx={{
                  px: 1.25, py: '3px',
                  fontFamily: C.mono, fontSize: 10, fontWeight: 600,
                  color: marginPct === p ? C.t1 : C.t4,
                  bgcolor: marginPct === p ? C.bg4 : 'transparent',
                  border: '1px solid', borderColor: marginPct === p ? C.b2 : C.b0,
                  borderRadius: 0.75, cursor: 'pointer',
                  transition: 'all 0.12s',
                  '&:hover': { borderColor: C.b2, color: C.t2, bgcolor: C.bg4 },
                }}
              >
                {p}%
              </Box>
            ))}
          </Box>

          {/* Exposure hint */}
          {marginPct > 0 && lev > 1 && (
            <Box sx={{
              px: 1.5, py: 1,
              bgcolor: C.bg1, border: '1px solid', borderColor: C.b0, borderRadius: 1.5,
            }}>
              <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, lineHeight: 1.6 }}>
                {effectiveExposureHint}
              </Typography>
            </Box>
          )}

          {/* Blow-up warning */}
          {marginPct > 20 && (
            <Box sx={{
              mt: 1, px: 1.5, py: 1,
              bgcolor: C.shortDim, border: '1px solid', borderColor: C.shortBdr, borderRadius: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 1,
            }}>
              <Warning sx={{ fontSize: 14, color: C.red, flexShrink: 0, mt: '1px' }} />
              <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.red, lineHeight: 1.5 }}>
                Risking {marginPct}% per trade. A 5-trade losing streak wipes {Math.min(100, (1 - Math.pow(1 - marginPct / 100, 5)) * 100).toFixed(0)}% of your account.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Fixed Notional mode ── */}
      {mode === 'fixed_notional' && (
        <Box>
          <Typography sx={microLabel}>POSITION SIZE</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small" type="number"
              value={newBot.fixedNotional ?? ''}
              onChange={e => patch({ fixedNotional: e.target.value })}
              placeholder="0"
              inputProps={{
                step: 10, min: 0,
                style: { fontFamily: C.mono, fontSize: 14, fontWeight: 600, padding: '7px 12px' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.t4 }}>
                      {quote.toUpperCase()}
                    </Typography>
                  </InputAdornment>
                ),
                sx: { ...fieldSx, height: 38 },
              }}
              sx={{ width: 180 }}
            />

            {fixedNotional > 0 && lev > 1 && (
              <Box sx={{
                px: 1.5, py: 1,
                bgcolor: C.bg1, border: '1px solid', borderColor: C.b0, borderRadius: 1.5,
              }}>
                <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4, lineHeight: 1 }}>
                  {(fixedNotional * lev).toLocaleString()} {quote} notional
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Max concurrent */}


    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
   ═══════════════════════════════════════════════════════════════════════════════ */

export default function PositionSizeSection({
  newBot,
  setNewBot,
  marketType = 'spot',
  leverage = 1,
  baseAsset,
  quoteAsset,
}) {
  const patch = (fields) => setNewBot(prev => ({ ...prev, ...fields }));
  const isFutures = marketType === 'futures';

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box sx={{ ...headerBarSx, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.blue, mr: 0, flexShrink: 0, opacity: 0.8 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
            POSITION SIZE
          </Typography>
          {isFutures && (
            <Chip
              size="small" label="FUTURES"
              sx={{ ml: 0.5, height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.futuresWarn, color: C.yellow, border: '1px solid', borderColor: C.futuresWarnBdr }}
            />
          )}
        </Box>

        {/* Summary pill */}
        {isFutures ? (
          <Box sx={{
            px: 1.25, py: '3px',
            bgcolor: C.bg4, border: '1px solid', borderColor: C.b2, borderRadius: 1,
          }}>
            <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t2 }}>
              {newBot.futureSizeMode === 'fixed_notional'
                ? `${newBot.fixedNotional || '—'} ${(quoteAsset || 'USDT').toUpperCase()}`
                : `${newBot.marginPct || '—'}% margin`
              }
            </Typography>
          </Box>
        ) : (
          newBot.tradeAmount && (
            <Box sx={{
              px: 1.25, py: '3px',
              bgcolor: C.bg4, border: '1px solid', borderColor: C.b2, borderRadius: 1,
            }}>
              <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t2 }}>
                  {newBot.tradeAmount} {((newBot.sizeMode || 'base') === 'base' ? (baseAsset || 'base') : (quoteAsset || 'USDT')).toUpperCase()}
              </Typography>
            </Box>
          )
        )}
      </Box>

      {isFutures ? (
        <FuturesSizePanel
          newBot={newBot}
          patch={patch}
          leverage={leverage}
          quoteAsset={quoteAsset}
        />
      ) : (
        <SpotSizePanel
          newBot={newBot}
          patch={patch}
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
        />
      )}
    </Paper>
  );
}